'use client'

import { BillingDataTable } from '@/components/billing/billing-data-table'
import type { BillingEmployee } from '@/components/billing/billing-table-columns'
import { billingTableColumns } from '@/components/billing/billing-table-columns'
import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { generateBillingPdf } from '@/lib/pdf_gen/billing-pdf'
import { useTRPC } from '@/trpc/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Building2, FileDown } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/super_admin/billing/')({
  component: RouteComponent,
})

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null)
  const [selectedBusinessIdsForBulk, setSelectedBusinessIdsForBulk] = useState<Set<string>>(
    new Set(),
  )
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const [isExportingBulkPdf, setIsExportingBulkPdf] = useState(false)

  const { data: organizations, isPending: isPendingOrgs } = useQuery({
    ...api.superAdmin.getOrganizations.queryOptions(),
  })

  const { data: employees, isPending: isPendingEmployees } = useQuery({
    ...api.superAdmin.getEmployeesForBilling.queryOptions({ businessId: selectedBusinessId! }),
    enabled: !!selectedBusinessId,
  })

  const selectedOrganization = organizations?.find((org) => org.businessId === selectedBusinessId)
  const businessName = selectedOrganization?.name ?? 'Organization'

  function toggleBulkSelection(businessId: string): void {
    setSelectedBusinessIdsForBulk((prev) => {
      const next = new Set(prev)
      if (next.has(businessId)) next.delete(businessId)
      else next.add(businessId)
      return next
    })
  }

  function toggleSelectAllBulk(): void {
    if (!organizations) return
    if (selectedBusinessIdsForBulk.size === organizations.length) {
      setSelectedBusinessIdsForBulk(new Set())
    } else {
      setSelectedBusinessIdsForBulk(new Set(organizations.map((o) => o.businessId)))
    }
  }

  async function handleExportPdf(employeesToExport: BillingEmployee[]): Promise<void> {
    setIsExportingPdf(true)
    const { ResultAsync } = await import('neverthrow')
    const result = await ResultAsync.fromPromise(
      generateBillingPdf(employeesToExport, businessName),
      (error) => error,
    )
    setIsExportingPdf(false)
    result.match(
      () => toast.success('PDF exported successfully'),
      (error) => {
        console.error('PDF export failed:', error)
        toast.error('Failed to export PDF')
      },
    )
  }

  async function handleBulkExportPdf(): Promise<void> {
    const ids = Array.from(selectedBusinessIdsForBulk)
    if (ids.length === 0) {
      toast.error('Select at least one business to export')
      return
    }
    setIsExportingBulkPdf(true)
    const { ResultAsync } = await import('neverthrow')
    const result = await ResultAsync.fromPromise(
      queryClient.fetchQuery(
        api.superAdmin.getEmployeesForBillingBatch.queryOptions({ businessIds: ids }),
      ),
      (error) => error,
    )
    if (result.isErr()) {
      setIsExportingBulkPdf(false)
      toast.error('Failed to fetch billing data')
      return
    }
    const batches = result.value
    let successCount = 0
    const delayMs = 500
    for (let i = 0; i < batches.length; i++) {
      const { businessName: name, employees: emps } = batches[i]
      const pdfResult = await ResultAsync.fromPromise(generateBillingPdf(emps, name), (e) => e)
      if (pdfResult.isOk()) successCount++
      if (i < batches.length - 1) {
        await new Promise((r) => setTimeout(r, delayMs))
      }
    }
    setIsExportingBulkPdf(false)
    toast.success(`Exported ${successCount} of ${batches.length} PDF(s)`)
  }

  if (isPendingOrgs) {
    return (
      <DashboardShell>
        <DashboardHeader heading="Billing" text="Active employees by organization" />
        <Skeleton className="h-40 w-full" />
      </DashboardShell>
    )
  }

  if (!organizations) return <div>Failed to load organizations</div>

  return (
    <DashboardShell>
      <DashboardHeader heading="Billing" text="Active employees by organization" />
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0" />
            <Select
              value={selectedBusinessId ?? ''}
              onValueChange={(value) => setSelectedBusinessId(value || null)}
            >
              <SelectTrigger className="w-[280px]">
                <SelectValue placeholder="Select a business to view billing" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.businessId} value={org.businessId}>
                    <div className="flex flex-col">
                      <span>{org.name}</span>
                      {org.type && (
                        <span className="text-xs text-muted-foreground">
                          {org.type === 'direct_employer' ? 'Direct Employer' : 'Staffing Agency'}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!selectedBusinessId ? (
            <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground">
              Select a business to view billing
            </div>
          ) : isPendingEmployees ? (
            <Skeleton className="h-40 w-full" />
          ) : !employees ? (
            <div className="rounded-md border p-4 text-muted-foreground">
              No employees found for this organization
            </div>
          ) : (
            <BillingDataTable
              columns={billingTableColumns}
              data={employees}
              onExportPdf={handleExportPdf}
              isExportingPdf={isExportingPdf}
            />
          )}
        </div>

        <div className="rounded-lg border p-4">
          <h3 className="mb-3 font-medium">Bulk export PDFs</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            Select multiple businesses to export a separate PDF for each.
          </p>
          <div className="mb-4 max-h-48 overflow-y-auto rounded-md border p-2">
            <div className="flex items-center gap-2 border-b pb-2">
              <Checkbox
                checked={
                  organizations.length > 0 &&
                  selectedBusinessIdsForBulk.size === organizations.length
                }
                onCheckedChange={toggleSelectAllBulk}
              />
              <span className="text-sm font-medium">Select all</span>
            </div>
            {organizations.map((org) => (
              <div
                key={org.businessId}
                className="flex cursor-pointer items-center gap-2 py-2 hover:bg-muted/50"
                onClick={() => toggleBulkSelection(org.businessId)}
              >
                <Checkbox
                  checked={selectedBusinessIdsForBulk.has(org.businessId)}
                  onCheckedChange={() => toggleBulkSelection(org.businessId)}
                />
                <span className="text-sm">{org.name}</span>
                {org.type && (
                  <span className="text-xs text-muted-foreground">
                    ({org.type === 'direct_employer' ? 'Direct' : 'Staffing'})
                  </span>
                )}
              </div>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkExportPdf}
            disabled={isExportingBulkPdf || selectedBusinessIdsForBulk.size === 0}
          >
            {isExportingBulkPdf ? (
              <span className="animate-pulse">Exporting...</span>
            ) : (
              <>
                <FileDown className="mr-2 h-4 w-4" />
                Export {selectedBusinessIdsForBulk.size} PDF(s)
              </>
            )}
          </Button>
        </div>
      </div>
    </DashboardShell>
  )
}
